import { AppLanguage } from '@anytag/shared-types';
import { useQueryHelper } from '@anytag/shared-hooks';
import { PostModal } from '@anytag/shared-molecules';
import { InstagramUGCManagementPostDetailsPayload } from '@anytag/graphql';
import { Anchor, Icon, Loading, Select, TextCutter } from '@anytag/shared-atoms';
import { checkIsReelPost, cn, localizedDateFormatter } from '@anytag/shared-utils';
import { UGCPostStatus } from '../../UGCPostStatus';
import { AnalyticsSelectedAccountInfo } from '../../../../types';
import { useUGCPostOptions, useUpdateUGCPostStatus } from '../../../hooks';

interface DefaultTemplateProps {
  analyticsAccount: AnalyticsSelectedAccountInfo;
  data: InstagramUGCManagementPostDetailsPayload;
}

export const DefaultTemplate = ({
  analyticsAccount,
  data: { status, postUrl, postSNSId, likes, comments, content, mediaURLs, postDate }
}: DefaultTemplateProps) => {
  const { t, i18n } = useQueryHelper();
  const { statusOptions } = useUGCPostOptions();
  const { handleChangeStatus, updating } = useUpdateUGCPostStatus({ postSNSId, analyticsAccount });

  return (
    <>
      {updating ? <Loading className="absolute top-0 left-0 z-5 m-0 size-full bg-white/40" /> : null}

      <div className="flex flex-1 gap-6">
        <div className="relative">
          <UGCPostStatus status={status} />
          <PostModal.MediaPreview images={mediaURLs} isReel={checkIsReelPost(postUrl)} />
        </div>

        <div className="flex flex-1 flex-col">
          <div className="flex gap-1">
            <p className="text-sm font-semibold">{t('Engagement')}</p>
            <PostModal.SocialLink className="mb-4" socialMedia={analyticsAccount.socialAccountType} postUrl={postUrl} />
          </div>
          <div className="flex">
            <PostModal.Statistics statistics={{ likes, comments }} />
            {postDate ? (
              <div className="ml-auto self-end text-base font-normal whitespace-nowrap">
                {localizedDateFormatter(postDate, 'PPP', i18n.language as AppLanguage)}
              </div>
            ) : null}
          </div>

          <PostModal.Caption caption={content} className="mt-4" />

          <p className="mt-4 text-sm font-semibold">{t("Let's track the permission process")}</p>
          <div className="mt-2 flex rounded-sm border-base-border bg-light-gray">
            <div className={styles.processItem}>
              <TextCutter lines={2} text={`1. ${t('Ask creator permission by your Instagram DM')}`} />
              <Anchor
                href={postUrl}
                target="_blank"
                className="mt-3 flex items-center text-base leading-none whitespace-nowrap text-blue-70"
              >
                {t('Contact via Instagram')}&nbsp;
                <Icon icon="external-link" size={12} variant="inherit" />
              </Anchor>
            </div>
            <div className={styles.processItem}>
              <TextCutter lines={1} text={`2. ${t('Update the status')}`} />
              <Select
                value={status}
                clearable={false}
                disabled={updating}
                className="mt-4.5"
                options={statusOptions}
                onChange={handleChangeStatus}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
const styles = {
  processItem: cn(
    'box-border flex-1',
    '[&_p]:text-sm [&_p]:font-normal',
    'first-of-type:relative first-of-type:z-1 first-of-type:bg-white first-of-type:pt-6 first-of-type:pr-4.75 first-of-type:pb-5.25 first-of-type:pl-2.75',
    'first-of-type:before:absolute first-of-type:before:top-0 first-of-type:before:-right-8 first-of-type:before:z-[-1] first-of-type:before:content-[""]',
    'first-of-type:before:border-l-white first-of-type:before:border-l-32',
    'first-of-type:before:border-t-55 first-of-type:before:border-t-transparent',
    'first-of-type:before:border-b-55 first-of-type:before:border-b-transparent',
    'last-of-type:pt-6 last-of-type:pr-4.75 last-of-type:pb-5.25 last-of-type:pl-12.5'
  )
};
